import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';

import { loadAutoSyncVisualizerScript } from './loadVisualizerScript';
import type { AutoSyncPartLoader, AutoSyncVisualizer, VisualizeVehicleParams } from './types';

export function createAutoSyncPartLoader(isTire?: boolean, useAutoSyncId?: boolean): AutoSyncPartLoader {
  if (isTire) {
    return {
      // tires can be loaded only on vehicles with suspensions enabled.
      // https://vvs.autosyncstudio.com/express/docs/#examples/example-loading-tires-code.html
      vehicleOptions: { suspension: true },
      load: (visualizer, autoSyncIdentifier) =>
        useAutoSyncId
          ? visualizer.loadTireById(autoSyncIdentifier)
          : visualizer.loadTireByPn(autoSyncIdentifier),
    };
  }

  return {
    load: (visualizer, autoSyncIdentifier) =>
      useAutoSyncId
        ? visualizer.loadWheelById(autoSyncIdentifier)
        : visualizer.loadWheelByPn(autoSyncIdentifier),
  };
}

export function isAutoSyncVisualizer(value: unknown): value is AutoSyncVisualizer {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (
    typeof v.loadVehicleById === 'function' &&
    typeof v.loadWheelByPn === 'function' &&
    typeof v.loadTireByPn === 'function' &&
    typeof v.loadWheelById === 'function' &&
    typeof v.loadTireById === 'function' &&
    typeof v.setHeight === 'function'
  );
}

export function visualizeVehicle(params: VisualizeVehicleParams): AutoSyncVisualizer | undefined {
  if (!window.initVvsExpress) {
    const { el, elId } = params.autoSyncConfig ?? {};
    const getTarget = () => el ?? (elId ? window.document.getElementById(elId) : null);
    const target = getTarget();

    void loadAutoSyncVisualizerScript()
      .then(() => {
        if (window.initVvsExpress && target?.isConnected && getTarget() === target) {
          visualizeVehicle(params);
        }
      })
      .catch((error: unknown) => {
        console.warn('Failed to initialize the AutoSync visualization', error);
      });
    return;
  }

  const {
    autoSyncConfig = {},
    vehicleId,
    autoSyncPartNumber,
    partLoader = createAutoSyncPartLoader(),
  } = params;

  if (!vehicleId) {
    return;
  }

  const candidate = window.initVvsExpress?.({
    apiUrl: 'https://api.autosyncstudio.com/',
    apiKey: fitmentSearchConfig.autoSyncKey,
    ...autoSyncConfig,
  });
  const visualizer = isAutoSyncVisualizer(candidate) ? candidate : undefined;

  if (!visualizer) {
    return;
  }

  const options = { ...(partLoader.vehicleOptions ?? {}) };

  // Vehicle must finish loading before tire/wheel PN — especially required for tires.
  visualizer
    .loadVehicleById(Number(vehicleId), options)
    .then(() => {
      if (!autoSyncPartNumber) {
        return;
      }

      return partLoader.load(visualizer, autoSyncPartNumber).catch((err: Error) => {
        visualizer.setHeight('0px');
        console.warn(`Failed to visualize the '${autoSyncPartNumber}' part. ${err.message}`);
      });
    })
    .catch((err: Error) => {
      visualizer.setHeight('0px');
      console.warn(`Failed to visualize the vehicle. Vehicle ID: ${vehicleId}. ${err.message}`);
    });

  return visualizer;
}
